import { AudioEngine } from './AudioEngine'
import type { ContextLike } from './AudioEngine'
import { browserStorage, createAudioSettings } from './settings'
import type { MusicId } from './sounds'

/**
 * The page's audio (PHASE3 T3.6): one engine and one settings store. Import `audio` to play;
 * `installAudio()` (once, from `main.tsx`) wires the gestures, M, the tab's visibility and the
 * button clicks.
 */
export const audioSettings = createAudioSettings(browserStorage())

function makeContext(): ContextLike<AudioBuffer> | null {
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  try {
    return new Ctor() as unknown as ContextLike<AudioBuffer>
  } catch {
    return null
  }
}

export const audio = new AudioEngine<AudioBuffer>({
  settings: audioSettings.store,
  createContext: makeContext,
  fill: (b, samples) => b.getChannelData(0).set(samples),
  fetcher: typeof fetch === 'function' ? (url, init) => fetch(url, init) : null,
  baseUrl: `${import.meta.env.BASE_URL}assets/audio/`,
  now: () => performance.now(),
})

/** Typing into a field never toggles mute. */
function isTextField(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false
  return t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName)
}

/** Music for a screen of the online flow (the race's own music starts with its countdown). */
export function musicForScreen(screen: string): MusicId | null | undefined {
  if (['menu', 'lobby', 'profile', 'settings', 'shop'].includes(screen)) return 'music_hub'
  return undefined
}

/** After the podium sting the hub music comes back. */
const STING_MS = 900

let stingTimer: ReturnType<typeof setTimeout> | null = null

/** The online flow moved to `screen`: hub music, or the results sting and then hub music. */
export function onScreen(screen: string): void {
  if (stingTimer) clearTimeout(stingTimer)
  stingTimer = null
  if (screen === 'results') {
    audio.play('podium')
    stingTimer = setTimeout(() => audio.loop('music_hub'), STING_MS)
    return
  }
  const music = musicForScreen(screen)
  if (music !== undefined) audio.loop(music)
}

let installed = false

/**
 * Unlock on the first gesture (browser autoplay rule), M toggles mute (not in text fields),
 * the hidden tab pauses music and drops SFX, and every button click clicks. `?debug` exposes
 * the engine as `window.runkr.audio`.
 */
export function installAudio(debug: boolean): void {
  if (installed) return
  installed = true
  const unlock = (): void => audio.unlock()
  for (const type of ['pointerdown', 'keydown', 'touchend']) {
    window.addEventListener(type, unlock, { capture: true, passive: true })
  }
  window.addEventListener('keydown', (e) => {
    if (e.code !== 'KeyM' || e.repeat || e.ctrlKey || e.metaKey || e.altKey) return
    if (isTextField(e.target)) return
    audioSettings.toggleMute()
  })
  document.addEventListener(
    'click',
    (e) => {
      const t = e.target
      if (t instanceof Element && t.closest('button, [role="button"]')) audio.play('click')
    },
    { capture: true },
  )
  document.addEventListener('visibilitychange', () => audio.setHidden(document.hidden))
  audio.setHidden(document.hidden)
  if (debug) debugHandle('audio', { engine: audio, settings: audioSettings, ...audio.stats })
}

/** Adds `key` to `window.runkr` (the `?debug` inspection handle) without replacing the rest. */
export function debugHandle(key: string, value: unknown): void {
  const w = window as unknown as { runkr?: Record<string, unknown> }
  w.runkr = { ...w.runkr, [key]: value }
}
